import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';

import AnnotationTooltip from './AnnotationTooltip';
import { RangeBar, RangeBandTint } from './AnnotationOverlay.styled';

// A time-range annotation drawn as a slim bar packed into a lane so overlapping
// ranges sit side-by-side rather than stacking translucent fills. Hovering the
// bar reveals the annotation tooltip plus a faint full-height tint marking the
// period's span on the trend.
function AnnotationRangeBand(props) {
  const {
    annotation, left, width, color, top, barHeight, plotHeight, showControls, onClick, onHoverChange
  } = props;

  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const positionTooltip = useCallback((event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    // Bars sit near the top of the plot, so a fixed 'bottom' anchor drops the
    // tooltip straight over the trend data. Flip adaptively like AnnotationMarker:
    // render above the bar unless it is close enough to the viewport top that an
    // upward tooltip would clip, in which case fall back to below.
    const viewportTopThreshold = 140;
    const placement = rect.top < viewportTopThreshold ? 'bottom' : 'top';
    const y = placement === 'top' ? rect.top : rect.bottom;

    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const margin = 12;
    const tooltipMaxWidth = 360;
    const cursorX = event.clientX;

    let align = 'center';
    let x = cursorX;
    if (cursorX - tooltipMaxWidth / 2 < margin) {
      align = 'left';
      x = Math.max(margin, cursorX);
    } else if (cursorX + tooltipMaxWidth / 2 > viewportWidth - margin) {
      align = 'right';
      x = Math.min(viewportWidth - margin, cursorX);
    }

    setTooltipPosition({ x, y, placement, align });
  }, []);

  const handleMouseEnter = useCallback((event) => {
    positionTooltip(event);
    setShowTooltip(true);
    if (onHoverChange) onHoverChange(annotation);
  }, [positionTooltip, onHoverChange, annotation]);

  const handleMouseMove = useCallback((event) => {
    if (showTooltip) positionTooltip(event);
  }, [showTooltip, positionTooltip]);

  const handleMouseLeave = useCallback(() => {
    setShowTooltip(false);
    if (onHoverChange) onHoverChange(null);
  }, [onHoverChange]);

  const handleClick = useCallback((event) => {
    if (onClick) onClick(annotation, event);
  }, [onClick, annotation]);

  return (
    <>
      {showTooltip && (
        <RangeBandTint
          left={left}
          width={width}
          color={color}
          height={plotHeight}
        />
      )}
      <RangeBar
        left={left}
        width={width}
        color={color}
        top={top}
        barHeight={barHeight}
        isHovered={showTooltip}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
      />
      {showTooltip && (
        <AnnotationTooltip
          annotation={annotation}
          position={tooltipPosition}
          showControls={showControls}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={handleMouseLeave}
        />
      )}
    </>
  );
}

AnnotationRangeBand.propTypes = {
  annotation: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    type: PropTypes.string.isRequired,
    start_time: PropTypes.string.isRequired,
    end_time: PropTypes.string
  }).isRequired,
  left: PropTypes.string.isRequired,
  width: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  top: PropTypes.string.isRequired,
  barHeight: PropTypes.string,
  plotHeight: PropTypes.string,
  showControls: PropTypes.bool,
  onClick: PropTypes.func,
  onHoverChange: PropTypes.func
};

AnnotationRangeBand.defaultProps = {
  barHeight: '7px',
  plotHeight: undefined,
  showControls: true,
  onClick: null,
  onHoverChange: null
};

export default AnnotationRangeBand;
